import React, { useEffect, useMemo, useState } from 'react';
import usePageTitle from 'hooks/usePageTitle';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import axios from 'axios';
import { backendURL } from 'config';
import { headerJson } from 'utilities/common';
import { LoadingOverlay } from 'dashboard/components/common';

// Map the internal actor kind onto MUI's semantic chip colors.
const KIND_COLOR = {
  owner: 'secondary',
  customer: 'primary',
  system: 'default',
};

const ACTION_LABEL = {
  'created-order': 'created order',
  'placed-order': 'placed order',
  'marked-paid': 'marked paid',
  'fulfilled-order': 'fulfilled',
  'cancelled-order': 'cancelled',
  'refunded-order': 'refunded',
  'created-product': 'created product',
  'updated-product': 'updated product',
  'deleted-product': 'deleted product',
  'created-page': 'created page',
  'updated-page': 'updated page',
  'added-member': 'added member',
  'invited-member': 'invited member',
  'accepted-invite': 'accepted invite',
  'revoked-invite': 'revoked invite',
  'removed-member': 'removed member',
  'updated-member-roles': 'updated roles',
};

function timeAgo(iso) {
  if (!iso) return '';
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initials(name) {
  if (!name) return '?';
  const parts = name.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export default function Activity() {
  usePageTitle('Activity', 'What has happened on your brand, newest first.');
  const [selectedBrand] = useOutletContext();
  const [items, setItems] = useState([]);
  const [kindFilter, setKindFilter] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listBrandActivity({ brand_id: selectedBrand, limit: 200 })
      .then((res) => { if (!cancelled) setItems(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load activity', { toastId: 'activity-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand]);

  const filtered = useMemo(() => {
    if (!kindFilter) return items;
    return items.filter((i) => i.actor_kind === kindFilter || i.target_kind === kindFilter);
  }, [items, kindFilter]);

  const exportCsv = async () => {
    try {
      const res = await axios.post(
        `${backendURL}/dashboard/activity/export-csv`,
        { brand_id: selectedBrand, limit: 5000 },
        { headers: headerJson(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-${String(selectedBrand).slice(-6)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Export failed');
    }
  };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '20px',
          p: { xs: '20px', md: '28px' },
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          flexWrap='wrap'
          gap='10px'
          mb='14px'
        >
          <Box>
            <Typography variant='h6' fontWeight='700'>Activity</Typography>
            <Typography variant='body2' color='text.secondary'>
              Who did what on this brand. Useful for auditing changes after the fact.
            </Typography>
          </Box>
          <Stack direction='row' spacing='8px' alignItems='center' flexWrap='wrap'>
            <FormControl size='small' sx={{ minWidth: 200 }}>
              <InputLabel shrink>Filter</InputLabel>
              <Select
                displayEmpty
                label='Filter'
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value)}
              >
                <MenuItem value=''>All activity</MenuItem>
                <MenuItem value='owner'>Owners</MenuItem>
                <MenuItem value='customer'>Customers</MenuItem>
                <MenuItem value='system'>System</MenuItem>
                <MenuItem value='order'>Order events</MenuItem>
                <MenuItem value='product'>Product changes</MenuItem>
              </Select>
            </FormControl>
            <Button
              size='small'
              variant='outlined'
              disabled={items.length === 0}
              onClick={exportCsv}
            >
              Export CSV
            </Button>
          </Stack>
        </Stack>

        {!selectedBrand && (
          <Typography variant='body2' color='text.secondary'>
            Select a brand to view its activity.
          </Typography>
        )}

        {selectedBrand && (
          <LoadingOverlay loading={loading && filtered.length === 0} minHeight={160}>
          <Stack spacing='10px'>
            {!loading && filtered.length === 0 && (
              <Typography variant='body2' color='text.secondary'>No activity recorded yet.</Typography>
            )}
            {filtered.map((row) => (
              <Stack
                key={row._id}
                direction='row'
                alignItems='flex-start'
                gap='12px'
                sx={{
                  p: '12px',
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Avatar
                  sx={{
                    width: 32, height: 32,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {initials(row.actor_label)}
                </Avatar>
                <Box flex='1' minWidth={0}>
                  <Stack direction='row' spacing='6px' mb='2px' flexWrap='wrap' alignItems='center'>
                    <Typography variant='body2' fontWeight='700'>
                      {row.actor_label || 'Unknown'}
                    </Typography>
                    <Chip
                      size='small'
                      label={row.actor_kind || 'system'}
                      color={KIND_COLOR[row.actor_kind] || 'default'}
                      sx={{ height: 18, fontSize: '0.65em' }}
                    />
                    <Typography variant='body2' color='text.secondary'>
                      {ACTION_LABEL[row.action] || row.action}
                    </Typography>
                    {row.target_kind && (
                      <Chip
                        size='small'
                        label={row.target_kind}
                        variant='outlined'
                        color='success'
                        sx={{ height: 16, fontSize: '0.6em' }}
                      />
                    )}
                  </Stack>
                  {row.summary && (
                    <Typography variant='body2' mt='2px'>{row.summary}</Typography>
                  )}
                </Box>
                <Typography variant='caption' color='text.secondary' whiteSpace='nowrap' mt='4px'>
                  {timeAgo(row.createdAt)}
                </Typography>
              </Stack>
            ))}
          </Stack>
          </LoadingOverlay>
        )}
      </Paper>
    </Box>
  );
}
